import { DestroyRef, Injectable, Signal, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { TimeagoIntl } from 'ngx-timeago';
import { strings as germanStrings } from 'ngx-timeago/language-strings/de.js';
import { strings as englishStrings } from 'ngx-timeago/language-strings/en.js';
import { strings as vietnameseStrings } from 'ngx-timeago/language-strings/vi.js';
import { strings as portugueseStrings } from 'ngx-timeago/language-strings/pt.js';
import { strings as russianStrings } from 'ngx-timeago/language-strings/ru.js';
import { strings as chineseStrings } from 'ngx-timeago/language-strings/zh-CN.js';
import { strings as frenchStrings } from 'ngx-timeago/language-strings/fr.js';
import { strings as italianStrings } from 'ngx-timeago/language-strings/it.js';
import { strings as ukrainianStrings } from 'ngx-timeago/language-strings/uk.js';
import { strings as koreanStrings } from 'ngx-timeago/language-strings/ko.js';
import { strings as spanishStrings } from 'ngx-timeago/language-strings/es.js';
import { strings as arabicStrings } from 'ngx-timeago/language-strings/ar.js';
import { strings as japaneseStrings } from 'ngx-timeago/language-strings/ja.js';
import { strings as turkishStrings } from 'ngx-timeago/language-strings/tr.js';
import { strings as polishStrings } from 'ngx-timeago/language-strings/pl.js';
import { strings as dutchStrings } from 'ngx-timeago/language-strings/nl.js';
import { strings as swedishStrings } from 'ngx-timeago/language-strings/sv.js';
import { strings as indonesianStrings } from 'ngx-timeago/language-strings/id.js';
import { strings as thaiStrings } from 'ngx-timeago/language-strings/th.js';
import { strings as romanianStrings } from 'ngx-timeago/language-strings/ro.js';
import { strings as czechStrings } from 'ngx-timeago/language-strings/cs.js';
import { strings as greekStrings } from 'ngx-timeago/language-strings/el.js';
import { strings as hungarianStrings } from 'ngx-timeago/language-strings/hu.js';
import { strings as finnishStrings } from 'ngx-timeago/language-strings/fi.js';
import { strings as danishStrings } from 'ngx-timeago/language-strings/da.js';
import { strings as norwegianStrings } from 'ngx-timeago/language-strings/no.js';
import { strings as bulgarianStrings } from 'ngx-timeago/language-strings/bg.js';
import { strings as slovakStrings } from 'ngx-timeago/language-strings/sk.js';
import { strings as croatianStrings } from 'ngx-timeago/language-strings/hr.js';
import { strings as serbianStrings } from 'ngx-timeago/language-strings/sr.js';
import { strings as persianStrings } from 'ngx-timeago/language-strings/fa.js';
import { SUPPORTED_LANGUAGES } from '@repo/models';
import { Subscription } from 'rxjs';
import { PosthogService } from 'src/services/posthog.service';

@Injectable({
  providedIn: 'root',
})
export class LanguageSelectionService {
  private readonly DEFAULT_LANGUAGE = 'de';
  private readonly destroyRef = inject(DestroyRef);
  private languageSubscription: Subscription | null = null;
  private language = signal<string>('de');
  private availableLanguages = signal<string[]>([]);

  constructor(
    private translateService: TranslateService,
    private readonly timeagoIntl: TimeagoIntl,
    private readonly posthogService: PosthogService
  ) {}

  public getLanguage(): Signal<string> {
    return this.language.asReadonly();
  }

  public setLanguage(language: string) {
    this.language.set(language);
    this.translateService.use(language);
    this.timeagoSetLanguage(language);
  }

  public getAvailableLanguages(): Signal<string[]> {
    return this.availableLanguages.asReadonly();
  }

  public initLanguage(languageCode?: string) {
    if (!languageCode || !SUPPORTED_LANGUAGES.includes(languageCode)) {
      languageCode = this.DEFAULT_LANGUAGE;
    }
    this.translateService.setFallbackLang(this.DEFAULT_LANGUAGE);

    if (!this.languageSubscription) {
      this.languageSubscription = this.posthogService
        .observeAvailableLanguages()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((posthogLanguages) => {
          const languages = posthogLanguages ?? SUPPORTED_LANGUAGES;
          this.translateService.addLangs(languages);
          this.availableLanguages.set(languages);

          if (!languages.includes(this.language())) {
            this.setLanguage(this.DEFAULT_LANGUAGE);
          }
        });
    }

    this.setLanguage(languageCode);
  }

  private timeagoSetLanguage(language: string) {
    switch (language) {
      case 'de':
        this.timeagoIntl.strings = germanStrings;
        break;
      case 'en':
        this.timeagoIntl.strings = englishStrings;
        break;
      case 'vi':
        this.timeagoIntl.strings = vietnameseStrings;
        break;
      case 'ko':
        this.timeagoIntl.strings = koreanStrings;
        break;
      case 'th':
        this.timeagoIntl.strings = thaiStrings;
        break;
      case 'id':
        this.timeagoIntl.strings = indonesianStrings;
        break;
      case 'sv':
        this.timeagoIntl.strings = swedishStrings;
        break;
      case 'nl':
        this.timeagoIntl.strings = dutchStrings;
        break;
      case 'pl':
        this.timeagoIntl.strings = polishStrings;
        break;
      case 'tr':
        this.timeagoIntl.strings = turkishStrings;
        break;
      case 'hi':
        this.timeagoIntl.strings = englishStrings;
        break;
      case 'ja':
        this.timeagoIntl.strings = japaneseStrings;
        break;
      case 'ar':
        this.timeagoIntl.strings = arabicStrings;
        break;
      case 'es':
        this.timeagoIntl.strings = spanishStrings;
        break;
      case 'ro':
        this.timeagoIntl.strings = romanianStrings;
        break;
      case 'cs':
        this.timeagoIntl.strings = czechStrings;
        break;
      case 'el':
        this.timeagoIntl.strings = greekStrings;
        break;
      case 'hu':
        this.timeagoIntl.strings = hungarianStrings;
        break;
      case 'fi':
        this.timeagoIntl.strings = finnishStrings;
        break;
      case 'da':
        this.timeagoIntl.strings = danishStrings;
        break;
      case 'no':
        this.timeagoIntl.strings = norwegianStrings;
        break;
      case 'bg':
        this.timeagoIntl.strings = bulgarianStrings;
        break;
      case 'sk':
        this.timeagoIntl.strings = slovakStrings;
        break;
      case 'hr':
        this.timeagoIntl.strings = croatianStrings;
        break;
      case 'sr':
        this.timeagoIntl.strings = serbianStrings;
        break;
      case 'fa':
        this.timeagoIntl.strings = persianStrings;
        break;
      case 'ms':
      case 'bn':
      case 'ta':
      case 'ur':
      case 'tl':
        this.timeagoIntl.strings = englishStrings;
        break;
      case 'pt':
        this.timeagoIntl.strings = portugueseStrings;
        break;
      case 'ru':
        this.timeagoIntl.strings = russianStrings;
        break;
      case 'zh':
        this.timeagoIntl.strings = chineseStrings;
        break;
      case 'fr':
        this.timeagoIntl.strings = frenchStrings;
        break;
      case 'it':
        this.timeagoIntl.strings = italianStrings;
        break;
      case 'uk':
        this.timeagoIntl.strings = ukrainianStrings;
        break;
      default:
        this.timeagoIntl.strings = germanStrings;
    }
    this.timeagoIntl.changes.next();
  }
}
